import React, { useCallback, useEffect, useState } from "react";
import api, { errMsg } from "../../core/api";
import RecordPage from "../../components/RecordPage";
import Modal from "../../components/Modal";
import Select from "../../components/Select";
import FormFields, { validateRequired } from "../../components/FormFields";
import ResponsiveList from "../../components/ResponsiveList";
import { currentJalali } from "../../components/JalaliDate";
import { useApp } from "../../core/AppContext";
import { fa, formatDate, todayIso } from "../../core/format";
import EquipmentRepairs from "./EquipmentRepairs";
import EquipmentWear from "./EquipmentWear";
import { equipmentPrint } from "../../core/printConfigs";

import EntityFiles, { attachmentsField, mainFromAttachments, afterSaveFor } from "../../components/EntityFiles";

const STATUS = [
  { value: "healthy", label: "سالم" },
  { value: "unusable", label: "غیرقابل استفاده" },
];
const STATUS_LABEL = { healthy: "سالم", unusable: "غیرقابل استفاده" };
const REQ_STATUS = { pending: "در انتظار تأیید", approved: "تأیید شد", rejected: "رد شد" };
const REQ_TONE = { pending: "warn", approved: "ok", rejected: "bad" };
const FIELD_LABEL = {
  code: "کد انبار",
  name: "نام تجهیزات",
  kind: "نوع تجهیزات",
  status: "وضعیت",
  location: "محل نصب / نگهداری",
  supervisorName: "مسئول محل",
  supervisorPhone: "شماره تماس مسئول",
  expiryDate: "انقضا / شارژ بعدی",
  inWarehouse: "انبار سالم",
  isProperty: "اموال",
  propertyNo: "شماره اموال",
  propertySignerId: "امضاکننده اموال",
};

const showVal = (k, v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (k === "status") return STATUS_LABEL[v] || v;
  if (k === "expiryDate") return formatDate(String(v).slice(0, 10));
  if (k === "inWarehouse") return String(v) === "1" ? "بله" : "خیر";
  return v;
};

const expiryBadge = (iso) => {
  if (!iso) return <span>—</span>;
  const today = todayIso();
  const diff = new Date(`${iso}T00:00:00Z`) - new Date(`${today}T00:00:00Z`);
  const cls = iso < today ? "bad" : diff <= 5 * 86400000 ? "warn" : "ok";
  const hint = iso < today ? "منقضی شده" : cls === "warn" ? "کمتر از ۳۰ روز" : "";
  return <span className={`badge solid ${cls}`} title={hint}>{formatDate(iso)}</span>;
};

const nowY = currentJalali().jy;


const WH = "انبار ایمنی";

// دو چک‌باکس انبار + علت فرسودگی (فیلد toWorn فقط مقدارش ارسال می‌شود)
function StorageBoxes({ form, setForm }) {
  const good = form.inWarehouse === "1";
  const worn = form.toWorn === "1";
  const setGood = (on) =>
    setForm((f) => ({
      ...f,
      inWarehouse: on ? "1" : "0",
      toWorn: on ? "0" : f.toWorn,
      location: on ? WH : f.location === WH ? "" : f.location,
    }));
  const setWorn = (on) =>
    setForm((f) => ({
      ...f,
      toWorn: on ? "1" : "0",
      inWarehouse: on ? "0" : f.inWarehouse,
      location: on && f.location === WH ? "" : f.location,
    }));
  return (
    <div className="field" style={{ gridColumn: "1 / -1" }}>
      <label>قرار دادن در انبار (اختیاری)</label>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <label className={`check-item ${good ? "on" : ""}`} style={{ margin: 0 }}>
          <input type="checkbox" checked={good} onChange={(e) => setGood(e.target.checked)} />
          انبار سالم
        </label>
        <label className={`check-item ${worn ? "on" : ""}`} style={{ margin: 0 }}>
          <input type="checkbox" checked={worn} onChange={(e) => setWorn(e.target.checked)} />
          انبار فرسوده (انتقال و خروج از فهرست تجهیزات)
        </label>
      </div>
      {good && <div className="hint">محل نگهداری «{WH}» ثبت می‌شود. برای نصب در محل دیگر، تیک را بردارید و محل را عوض کنید.</div>}
      {worn && (
        <>
          <div className="alert info" style={{ marginTop: 8 }}>
            با ذخیره، تجهیز از این فهرست خارج و به بخش فرسوده‌ی انبار ایمنی منتقل می‌شود.
          </div>
          <label>علت فرسودگی<span className="req">*</span></label>
          <textarea rows={3} value={form.wearReason || ""} onChange={(e) => setForm((f) => ({ ...f, wearReason: e.target.value }))} />
        </>
      )}
    </div>
  );
}
function PropertyBox({ form, setForm, errors }) {
  const [emps, setEmps] = useState([]);
  useEffect(() => {
    api.get("/employees").then((r) => setEmps(r.data.items)).catch(() => {});
  }, []);
  const on = form.isProperty === "1";
  return (
    <div className="field" style={{ gridColumn: "1 / -1" }}>
      <label className={`check-item ${on ? "on" : ""}`} style={{ margin: 0, display: "inline-flex" }}>
        <input
          type="checkbox"
          checked={on}
          onChange={(e) =>
            setForm((f) => ({
              ...f,
              isProperty: e.target.checked ? "1" : "0",
              ...(e.target.checked ? {} : { propertyNo: "", propertySignerId: "" }),
            }))
          }
        />
        اموال
      </label>
      {on && (
        <div className="grid2" style={{ marginTop: 10 }}>
          <div className="field">
            <label>شماره اموال<span className="req">*</span></label>
            <input
              className="ltr"
              maxLength={60}
              value={form.propertyNo || ""}
              onChange={(e) => setForm((f) => ({ ...f, propertyNo: e.target.value }))}
            />
          </div>
          <div className="field">
            <label>نام کاربر امضاکننده (تحویل‌گیرنده)<span className="req">*</span></label>
            <Select
              searchable
              value={form.propertySignerId || ""}
              onChange={(v) => setForm((f) => ({ ...f, propertySignerId: v }))}
              options={emps.map((e) => ({ value: e.id, label: `${e.firstName} ${e.lastName}` }))}
              placeholder="انتخاب کنید..."
            />
          </div>
        </div>
      )}
    </div>
  );
}
const baseFields = [
  { name: "code", label: "کد انبار", ltr: true, hint: "اختیاری؛ اگر خالی بگذارید خودکار ساخته می‌شود (مثلاً EQ-0007). برای چند عدد از یک مدل، به هرکدام کد جدا بدهید." },
  { name: "name", label: "نام تجهیزات", required: true },
  { name: "kind", label: "نوع تجهیزات", required: true },
  { name: "status", label: "وضعیت تجهیزات", type: "select", options: STATUS, required: true },
  { name: "location", label: "محل نصب یا نگهداری", required: true },
  { name: "supervisorName", label: "مسئول محل", required: true },
  { name: "supervisorPhone", label: "شماره تماس مسئول محل", type: "mobile", required: true },
  { name: "expiryDate", label: "تاریخ انقضا یا شارژ بعدی", type: "date", full: true, minYear: nowY - 5, maxYear: nowY + 15 },
];
const adminFields = [
  ...baseFields,
  { name: "inWarehouse", label: "انبار", type: "custom", full: true, render: (p) => <StorageBoxes {...p} /> },
  { name: "toWorn", label: "انتقال به فرسوده", type: "custom", render: () => null },
  { name: "wearReason", label: "علت فرسودگی", type: "custom", render: () => null },
  { name: "propertyNo", label: "شماره اموال", type: "custom", render: () => null },
  { name: "propertySignerId", label: "امضاکننده", type: "custom", render: () => null },
  attachmentsField("عکس‌ها و فایل‌های تجهیزات (اختیاری، چند فایل مجاز است)"),
  { name: "isProperty", label: "اموال", type: "custom", full: true, render: (p) => <PropertyBox {...p} /> },
];
const proposeFields = [
  ...baseFields,
  { name: "inWarehouse", label: "قرار گرفتن در انبار سالم", type: "select", options: [{ value: "0", label: "خیر" }, { value: "1", label: "بله" }] },
  { name: "reason", label: "علت ویرایش", type: "textarea", required: true, full: true, placeholder: "چرا این تغییرات لازم است؟" },
];

/* ---------- نمایش تغییرات (از ← به) ---------- */
function ChangesView({ changes }) {
  return (
    <div style={{ display: "grid", gap: 4 }}>
      {Object.entries(changes || {}).map(([k, c]) => (
        <div key={k} style={{ fontSize: 13 }}>
          <b>{FIELD_LABEL[k] || k}:</b> {showVal(k, c.from)} <i className="fa-solid fa-arrow-left" style={{ margin: "0 4px", fontSize: 11 }} /> <b>{showVal(k, c.to)}</b>
        </div>
      ))}
    </div>
  );
}

/* ---------- جزئیات + سابقه تغییرات ---------- */
function DetailModal({ row, onClose }) {
  const items = [
    ["کد انبار", row.code],
    ["نام تجهیزات", row.name],
    ["نوع تجهیزات", row.kind],
    ["وضعیت", STATUS_LABEL[row.status]],
    ["محل نصب / نگهداری", row.location],
    ["مسئول محل", row.supervisorName],
    ["شماره تماس مسئول", row.supervisorPhone],
    ["انقضا / شارژ بعدی", row.expiryDate ? formatDate(row.expiryDate) : "—"],
  ];
  return (
    <Modal wide title={`جزئیات — ${row.name}`} onClose={onClose}
      footer={<button className="btn btn-ghost" onClick={onClose}>بستن</button>}>
      <div className="grid2">
        {items.map(([l, v]) => (
          <div className="field" key={l}><label>{l}</label><div dir={l.includes("تماس") ? "ltr" : undefined} style={{ textAlign: l.includes("تماس") ? "right" : undefined }}>{v || "—"}</div></div>
        ))}
      </div>
      <div className="field">
        <label>توضیحات و سابقه‌ی تغییرات</label>
        <div style={{ whiteSpace: "pre-wrap", lineHeight: 2, background: "var(--card-2)", border: "1px solid var(--border)", borderRadius: 10, padding: "10px 14px", minHeight: 60 }}>
          {row.notes || <span className="hint">تغییری ثبت نشده است.</span>}
        </div>
      </div>
    </Modal>
  );
}

/* ---------- ثبت درخواست ویرایش (کاربر) ---------- */
function ProposeModal({ row, onClose, onDone }) {
  const { toast } = useApp();
  const [form, setForm] = useState({
    code: row.code || "", name: row.name, kind: row.kind, status: row.status, location: row.location,
    supervisorName: row.supervisorName, supervisorPhone: row.supervisorPhone,
    expiryDate: row.expiryDate || "", reason: "",
  });
  const [errors, setErrors] = useState({});
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const errs = validateRequired(proposeFields, form);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true); setErr("");
    try {
      await api.post(`/equipment/${row.id}/change-request`, form);
      toast("درخواست ویرایش برای تأیید مسئول ایمنی ارسال شد");
      onDone();
    } catch (e) { setErr(errMsg(e)); setBusy(false); }
  };

  return (
    <Modal title={`درخواست ویرایش — ${row.name}`} onClose={() => !busy && onClose()}
      footer={<>
        <button className="btn" onClick={submit} disabled={busy}>{busy ? "در حال ارسال..." : "ارسال برای تأیید"}</button>
        <button className="btn btn-ghost" onClick={onClose} disabled={busy}>انصراف</button>
      </>}>
      <div className="alert info">تغییرات شما بعد از تأیید مسئول ایمنی اعمال می‌شود؛ در صورت رد شدن، هیچ تغییری ثبت نخواهد شد.</div>
      {err && <div className="alert err">{err}</div>}
      <FormFields fields={proposeFields} form={form} setForm={setForm} errors={errors} />
    </Modal>
  );
}

/* ---------- رد درخواست ---------- */
function RejectModal({ onClose, onSubmit }) {
  const [text, setText] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    if (text.trim().length < 3) return setErr("دلیل رد را بنویسید");
    setBusy(true); setErr("");
    try { await onSubmit(text.trim()); } catch (e) { setErr(errMsg(e)); setBusy(false); }
  };
  return (
    <Modal small title="رد درخواست ویرایش" onClose={() => !busy && onClose()}
      footer={<>
        <button className="btn btn-danger" onClick={go} disabled={busy}>{busy ? "..." : "رد درخواست"}</button>
        <button className="btn btn-ghost" onClick={onClose} disabled={busy}>انصراف</button>
      </>}>
      {err && <div className="alert err">{err}</div>}
      <div className="field"><label>دلیل رد (برای کاربر نمایش داده می‌شود)</label>
        <textarea rows={4} maxLength={500} value={text} onChange={(e) => setText(e.target.value)} />
      </div>
    </Modal>
  );
}

/* ---------- فهرست درخواست‌های ویرایش ---------- */
function ChangeRequests({ admin, onChanged }) {
  const { toast, confirm } = useApp();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("pending");
  const [rejectFor, setRejectFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/equipment/change-requests", { params: { status: status || undefined } });
      setRows(data.items);
    } catch (e) { toast(errMsg(e), "err"); } finally { setLoading(false); }
  }, [status, toast]);
  useEffect(() => { load(); }, [load]);

  const approve = async (r) => {
    if (!(await confirm("تغییرات اعمال و در سابقه‌ی تجهیزات ثبت شود؟", { okText: "تأیید و اعمال", danger: false }))) return;
    try {
      await api.post(`/equipment/change-requests/${r.id}/decision`, { approve: true });
      toast("تغییرات اعمال شد");
      load(); onChanged();
    } catch (e) { toast(errMsg(e), "err"); }
  };
  const reject = async (note) => {
    await api.post(`/equipment/change-requests/${rejectFor.id}/decision`, { approve: false, note });
    toast("درخواست رد شد؛ تغییری اعمال نشد");
    setRejectFor(null);
    load();
  };

  const when = (r) => `${formatDate(String(r.createAt).slice(0, 10))} ${fa(String(r.createAt).slice(11, 16))}`;
  const statusBadge = (r) => <span className={`badge ${REQ_TONE[r.status]}`}>{REQ_STATUS[r.status]}</span>;

  return (
    <>
      <div className="toolbar">
        <div className="field">
          <label>وضعیت درخواست</label>
          <Select value={status} onChange={setStatus}
            options={[{ value: "", label: "همه" }, ...Object.entries(REQ_STATUS).map(([value, label]) => ({ value, label }))]} />
        </div>
      </div>
      <ResponsiveList
        loading={loading}
        rows={rows}
        empty="درخواستی یافت نشد"
        columns={[
          { key: "createAt", label: "تاریخ ثبت", render: when },
          { key: "equipmentName", label: "تجهیزات", render: (r) => <b>{r.equipmentName}</b> },
          { key: "requesterName", label: "درخواست‌دهنده" },
          { key: "changes", label: "تغییرات", render: (r) => <ChangesView changes={r.changes} /> },
          { key: "reason", label: "علت ویرایش", render: (r) => <div style={{ maxWidth: 240 }}>{r.reason}</div> },
          {
            key: "status", label: "وضعیت",
            render: (r) => (
              <div style={{ display: "grid", gap: 4, justifyItems: "start" }}>
                {statusBadge(r)}
                {r.status !== "pending" && r.deciderName && <small className="hint">{r.deciderName}</small>}
                {r.decisionNote && <small className="hint">{r.decisionNote}</small>}
              </div>
            ),
          },
        ]}
        mobileTitle={(r) => r.equipmentName}
        mobileBadge={statusBadge}
        mobileMeta={(r) => [
          { icon: "fa-regular fa-calendar", label: "تاریخ", value: when(r) },
          { icon: "fa-solid fa-user", label: "درخواست‌دهنده", value: r.requesterName },
          { icon: "fa-solid fa-pen", full: true, value: <ChangesView changes={r.changes} /> },
          { icon: "fa-regular fa-comment", full: true, label: "علت", value: r.reason },
          ...(r.decisionNote ? [{ icon: "fa-solid fa-gavel", full: true, label: r.deciderName || "نتیجه", value: r.decisionNote }] : []),
        ]}
        actions={admin ? (r) => r.status === "pending" && (
          <>
            <button className="btn btn-sm" onClick={() => approve(r)}><i className="fa-solid fa-check" /> تأیید</button>
            <button className="btn btn-sm btn-danger" onClick={() => setRejectFor(r)}><i className="fa-solid fa-xmark" /> رد</button>
          </>
        ) : null}
      />
      {rejectFor && <RejectModal onClose={() => setRejectFor(null)} onSubmit={reject} />}
    </>
  );
}

export default function Equipment() {
  const { user } = useApp();
  const admin = user.role === "admin";
  const staff = user.role === "staff";
  const [tab, setTab] = useState("list");
  const [tick, setTick] = useState(0);
  const [detail, setDetail] = useState(null);
  const [propose, setPropose] = useState(null);
  const [pending, setPending] = useState(0);
  const [repPending, setRepPending] = useState(0);
  const [wearPending, setWearPending] = useState(0);
  const [filesFor, setFilesFor] = useState(null);
  useEffect(() => {
    if (!admin) return;
    api.get("/equipment/change-requests/count").then((r) => setPending(r.data.count || 0)).catch(() => {});
    api.get("/equipment-repairs/pending-count").then((r) => setRepPending(r.data.count || 0)).catch(() => {});
    api.get("/equipment-wear/pending-count").then((r) => setWearPending(r.data.count || 0)).catch(() => {});
  }, [admin, tab, tick]);

  return (
    <>
      <div className="tabs">
        <button className={`tab-btn ${tab === "list" ? "on" : ""}`} onClick={() => setTab("list")}>فهرست تجهیزات</button>
        <button className={`tab-btn ${tab === "requests" ? "on" : ""}`} onClick={() => setTab("requests")}>
          {staff ? "درخواست‌های ویرایش من" : "درخواست‌های ویرایش"}{admin && pending > 0 ? ` (${fa(pending)})` : ""}
        </button>
        <button className={`tab-btn ${tab === "repairs" ? "on" : ""}`} onClick={() => setTab("repairs")}>
          تعمیرات و نوسازی{admin && repPending > 0 ? ` (${fa(repPending)})` : ""}
        </button>
        <button className={`tab-btn ${tab === "wear" ? "on" : ""}`} onClick={() => setTab("wear")}>
          فرسودگی{admin && wearPending > 0 ? ` (${fa(wearPending)})` : ""}
        </button>
      </div>

      {tab === "list" && (
        <RecordPage
        printReport={equipmentPrint}
          key={tick}
          title="تجهیزات"

          subtitle={
            admin ? "ثبت و مدیریت تجهیزات ایمنی و آتش‌نشانی"
              : staff ? "مشاهده‌ی تجهیزات و ثبت درخواست ویرایش (پس از تأیید مسئول ایمنی اعمال می‌شود)"
                : "مشاهده‌ی تجهیزات و سابقه‌ی تغییرات"
          }
          endpoint="/equipment"
          createLabel="افزودن تجهیزات"
          multipart
          canCreate={admin}
          canEdit={admin}
          canDelete={admin}
          initial={{ status: "healthy", inWarehouse: "0", toWorn: "0", isProperty: "0" }}

          filters={[{ name: "status", label: "وضعیت", options: STATUS }]}
          toForm={(r) => ({
            ...r,
            expiryDate: r.expiryDate || "",
            file: null,
            inWarehouse: r.inWarehouse ? "1" : "0",
            toWorn: "0",
            wearReason: "",
            isProperty: r.isProperty ? "1" : "0",
            propertyNo: r.propertyNo || "",
            propertySignerId: r.propertySignerId ?? "",
          })}
          fields={adminFields}
          rowExtra={(r) => (
            <>
              <button className="icon-btn" title="فایل‌ها و تصاویر" onClick={() => setFilesFor(r)}><i className="fa-solid fa-folder-open" /></button>
              <button className="icon-btn" title="جزئیات و سابقه‌ی تغییرات" onClick={() => setDetail(r)}><i className="fa-solid fa-circle-info" /></button>
              {staff && <button className="icon-btn" title="درخواست ویرایش" onClick={() => setPropose(r)}><i className="fa-solid fa-pen-to-square" /></button>}
            </>
          )}
          columns={[
            { key: "code", label: "کد انبار", render: (r) => <b dir="ltr">{r.code || "—"}</b> },
            { key: "name", label: "نام تجهیزات", render: (r) => <b>{r.name}</b> },
            { key: "kind", label: "نوع تجهیزات" },
            {
              key: "status", label: "وضعیت",
              render: (r) => <span className={`badge ${r.status === "healthy" ? "ok" : "bad"}`}>{STATUS_LABEL[r.status]}</span>,
            },
            { key: "inWarehouse", label: "انبار", render: (r) => (r.inWarehouse ? <span className="badge wait">انبار سالم</span> : "—") },
            { key: "location", label: "محل نصب / نگهداری" },
            { key: "supervisorName", label: "مسئول محل" },
            { key: "supervisorPhone", label: "شماره تماس مسئول", render: (r) => <span dir="ltr">{r.supervisorPhone || "—"}</span> },
            { key: "expiryDate", label: "انقضا / شارژ بعدی", render: (r) => expiryBadge(r.expiryDate) },
          ]}

          hideFileButton
          transformBody={mainFromAttachments}
          afterSave={afterSaveFor("equipment")}
        />
      )}

      {tab === "requests" && (
        <>
          <div className="page-head">
            <div>
              <h2>درخواست‌های ویرایش تجهیزات</h2>
              <p>{admin ? "تأیید یا رد ویرایش‌های پیشنهادی کاربران" : staff ? "وضعیت درخواست‌هایی که ثبت کرده‌اید" : "مشاهده‌ی درخواست‌های ویرایش"}</p>
            </div>
          </div>
          <ChangeRequests admin={admin} onChanged={() => setTick((t) => t + 1)} />
        </>
      )}

      {tab === "repairs" && <EquipmentRepairs onChanged={() => setTick((t) => t + 1)} />}
      {tab === "wear" && <EquipmentWear onChanged={() => setTick((t) => t + 1)} />}
      {filesFor && (
        <EntityFiles type="equipment" id={filesFor.id} title={filesFor.name}
          mainPath={filesFor.hasFile ? `/equipment/${filesFor.id}/file` : null}
          canAdd={admin} canRemove={admin}
          onClose={() => setFilesFor(null)} onChanged={() => setTick((t) => t + 1)} />
      )}
      {detail && <DetailModal row={detail} onClose={() => setDetail(null)} />}
      {propose && <ProposeModal row={propose} onClose={() => setPropose(null)} onDone={() => { setPropose(null); setTab("requests"); }} />}
    </>
  );
}